import {createRouter, createWebHistory} from 'vue-router';
import type {Role} from '@afroep/core/domain/role';
import {watch} from 'vue';
import {i18n} from '@/i18n';
import {applyDocumentTitle} from '@/router/document-title';
import {resolveNavigation} from '@/router/guard';
import {routes} from '@/router/routes';
import {useSessionStore} from '@/stores/session';

declare module 'vue-router' {
  interface RouteMeta {
    /** Absent means "any signed-in user". `public` routes skip the guard entirely. */
    roles?: readonly Role[];
    public?: boolean;
    /** Translation key, not a title: the browser tab follows the chosen language too. */
    titleKey?: string;
  }
}

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async to => {
  const session = useSessionStore();
  if (session.status === 'unknown') await session.load();
  return resolveNavigation({isAuthenticated: session.isAuthenticated, role: session.role}, to);
});

/**
 * The browser tab follows both the route and the language. The second listener is the one
 * that is easy to miss: the switcher changes the language without navigating anywhere, so
 * `afterEach` alone would leave the tab in the previous language until the next click.
 */
router.afterEach(to => {
  applyDocumentTitle(to.meta.titleKey);
});

watch(i18n.global.locale, () => {
  applyDocumentTitle(router.currentRoute.value.meta.titleKey);
});
